import { IAuthRepository, type AuthResult } from '../../domain/repositories/IAuthRepository';
import { EmailDomainNotAllowedError } from '@/utils/emailDomains';
import { UserInactiveError } from '@/utils/userCompanyStatus';
import type { InviteData } from '../../domain/types/Company';
import type { CompanyRole, ModulePermissions } from '../../domain/types/Role';

export class AuthService {
  constructor(private authRepository: IAuthRepository) {}

  async createUser(userData: {
    email: string
    password: string
    name: string
    lastName: string
    hasCompletedOnboarding: boolean,
    companyId?: string | null,
    role?: CompanyRole,
    permissions?: ModulePermissions,
    customRoleId?: string,
    inviteId?: string | null
  }): Promise<AuthResult> {
    return this.authRepository.createUser(userData)
  }

  async login(email: string, password: string): Promise<AuthResult> {
    try {
      const userData = await this.authRepository.login(email, password);
      return {
        success: !!userData,
        user: userData ?? undefined,
        error: userData ? undefined : 'Usuario no encontrado'
      };
    } catch (error: any) {
      // El bloqueo por dominio y el de usuario inactivo se propagan: la vista necesita
      // explicarle al usuario por qué no entró, en vez de mostrarle un error de credenciales.
      if (error instanceof EmailDomainNotAllowedError || error instanceof UserInactiveError) throw error;
      return {
        success: false,
        error: error.message
      };
    }
  }

  async loginWithProvider(
    provider: 'google' | 'microsoft',
    inviteData?: InviteData
  ): Promise<AuthResult> {
    try {
      const userData = await this.authRepository.loginWithProvider(provider, inviteData);
      return {
        success: !!userData,
                user: userData || undefined,
        error: userData ? undefined : 'Usuario no encontrado'
      };
    } catch (error: any) {
      if (error instanceof EmailDomainNotAllowedError || error instanceof UserInactiveError) throw error;
      return {
        success: false,
        error: error.message
      };
    }
  }

  async logout(): Promise<void> {
    return this.authRepository.logout();
  }

  async getCurrentUser(): Promise<AuthResult['user'] | null> {
    return this.authRepository.getCurrentUser();
  }

  async updateUser(userId: string, userData: Partial<AuthResult['user']>): Promise<AuthResult['user']> {
    return this.authRepository.updateUser(userId, userData);
  }
} 